/**
 * Fill in the Blanks element schema
 */

import type {
    ElementPlayerProps,
    ElementSchema,
    TextFieldSchema,
    GroupFieldSchema,
    ListFieldSchema,
} from '@ai-content-hub/content-kit';
import type { TiptapJSON } from '@ai-content-hub/content-kit';
import type { ContentConfig } from '@ai-content-hub/content-core';
import { getDefaultConfig } from '@ai-content-hub/content-core';
import map from 'lodash/map';
import size from 'lodash/size';
import forEach from 'lodash/forEach';
import isArray from 'lodash/isArray';

export const defaultFitbElementI18n = {
    en: {
        content: 'Content',
        configuration: 'Configuration',
        fillInTheBlanks: 'Fill in the blanks',
        answerMode: 'Answer mode',
        blankModeType: 'Type (free text)',
        blankModeDropdown: 'Dropdown',
        blankModeDrag: 'Drag & drop',
        shuffleOptionsFITB: 'Shuffle options',
        shuffleFITBDesc: 'Randomize chips and dropdown order',
        caseSensitiveFITB: 'Case sensitive',
        caseSensitiveDesc: 'Typed answers must match capitalization',
        passageLabelFITB: 'Passage',
        passageFITBHint:
            'Use three or more underscores (___) for each blank. Tables and formatting are supported.',
        blanksHeading: 'Blanks',
        blanksEmptyFITB: 'Add ___ markers in the passage to create blanks.',
        blankNth: 'Blank',
        acceptedAnswers: 'Accepted answers',
        wrongOptionsFITB: 'Wrong options',
        wrongOptionsSemicolonHint: 'Wrong options; separate with semicolons',
        correctAnswersSemicolonHint:
            'Correct answer(s); separate with semicolons',
        explanationAfterSubmit: 'Explanation (after submit)',
        explainCorrectAnswer: 'Explain the correct answer...',
        choosePlaceholder: 'Choose…',
        fitbDragInstructions:
            'Fill in the blanks with the correct answers',
        fitbRemoveAnswerAria: 'Remove answer for blank {n}',
    },
    vi: {
        content: 'Nội dung',
        configuration: 'Cấu hình',
        fillInTheBlanks: 'Điền vào chỗ trống',
        answerMode: 'Chế độ trả lời',
        blankModeType: 'Nhập (văn bản tự do)',
        blankModeDropdown: 'Chọn danh sách',
        blankModeDrag: 'Kéo & thả',
        shuffleOptionsFITB: 'Xáo trộn tùy chọn',
        shuffleFITBDesc:
            'Xáo trộn ngẫu nhiên thứ tự chip và dropdown',
        caseSensitiveFITB: 'Phân biệt chữ hoa chữ thường',
        caseSensitiveDesc:
            'Câu trả lời phải đúng chữ hoa/thường',
        passageLabelFITB: 'Đoạn văn',
        passageFITBHint:
            'Dùng ba hoặc nhiều gạch dưới (___) cho mỗi chỗ trống. Hỗ trợ bảng và định dạng.',
        blanksHeading: 'Chỗ trống',
        blanksEmptyFITB:
            'Thêm dấu ___ vào đoạn văn để tạo chỗ trống.',
        blankNth: 'Chỗ trống',
        acceptedAnswers: 'Đáp án chấp nhận',
        wrongOptionsFITB: 'Tùy chọn sai',
        wrongOptionsSemicolonHint:
            'Tùy chọn sai; phân cách bằng dấu chấm phẩy',
        correctAnswersSemicolonHint:
            'Đáp án đúng; phân tách bằng dấu chấm phẩy',
        explanationAfterSubmit: 'Giải thích (sau khi nộp)',
        explainCorrectAnswer: 'Giải thích đáp án đúng...',
        choosePlaceholder: 'Chọn…',
        fitbDragInstructions:
            'Điền vào chỗ trống bằng các đáp án đúng',
        fitbRemoveAnswerAria: 'Xóa đáp án cho chỗ trống {n}',
    },
};

export type FITBBlankMode = 'drag' | 'dropdown' | 'type';

export interface FITBBlank {
    id: string;
    acceptedAnswers: string[];
    distractors?: string[];
    placeholder?: string;
}

export interface FITBElementData {
    passage: TiptapJSON;
    blanks: FITBBlank[];
    explanation?: string;
    /**
     * Mirrored from config.caseSensitive for type mode; grader only receives scoring config from ContentPlayer.
     */
    gradingCaseSensitive?: boolean;
}

export type FITBAnswer = Record<string, string>;

export interface FITBConfig extends ContentConfig {
    blankMode?: FITBBlankMode;
    caseSensitive?: boolean;
    shuffleOptions?: boolean;
}

/** Config object passed to the FITB player from `ElementPlayerProps` (looser than `ContentConfig`). */
export type FITBElementPlayerConfig = NonNullable<
    ElementPlayerProps<FITBElementData, FITBAnswer>['config']
>;

interface WithBlankMode {
    blankMode?: FITBBlankMode;
}

interface WithCaseSensitive {
    caseSensitive?: boolean;
}

interface WithShuffleOptions {
    shuffleOptions?: boolean;
}

function isWithBlankMode(c: object | null | undefined): c is WithBlankMode {
    return c !== null && typeof c === 'object' && 'blankMode' in c;
}

function isWithCaseSensitive(c: object | null | undefined): c is WithCaseSensitive {
    return c !== null && typeof c === 'object' && 'caseSensitive' in c;
}

function isWithShuffleOptions(c: object | null | undefined): c is WithShuffleOptions {
    return c !== null && typeof c === 'object' && 'shuffleOptions' in c;
}

/** Match three or more underscores as a blank marker */
export const FITB_BLANK_MARKER_REGEX = /_{3,}/g;

export function blankIdForIndex(index: number): string {
    return `blank-${index}`;
}

export function createDefaultBlank(id: string): FITBBlank {
    return {
        id,
        acceptedAnswers: [''],
        distractors: [],
        placeholder: '',
    };
}

export function countBlanksInTiptap(doc: TiptapJSON | undefined): number {
    if (!doc) return 0;
    let count = 0;
    const walk = (node: TiptapJSON) => {
        if (node.type === 'text' && typeof node.text === 'string') {
            const matches = node.text.match(FITB_BLANK_MARKER_REGEX);
            if (matches) count += size(matches);
        }
        if (isArray(node.content)) {
            forEach(node.content, walk);
        }
    };
    if (isArray(doc.content)) {
        forEach(doc.content, walk);
    }
    return count;
}

/**
 * Underscore count per blank marker in document order (e.g. `___` → 3, `______` → 6).
 */
export function blankMarkerLengthsInPassageOrder(doc: TiptapJSON | undefined): number[] {
    if (!doc) return [];
    const lengths: number[] = [];
    const walk = (node: TiptapJSON) => {
        if (node.type === 'text' && typeof node.text === 'string') {
            const re = new RegExp(FITB_BLANK_MARKER_REGEX.source, 'g');
            let m: RegExpExecArray | null;
            while ((m = re.exec(node.text)) !== null) {
                lengths.push(m[0].length);
            }
        }
        if (isArray(node.content)) {
            forEach(node.content, walk);
        }
    };
    if (isArray(doc.content)) {
        forEach(doc.content, walk);
    }
    return lengths;
}

export function syncBlanksWithPassage(data: Partial<FITBElementData>): FITBBlank[] {
    const passage = data.passage ?? { type: 'doc', content: [{ type: 'paragraph' }] };
    const n = countBlanksInTiptap(passage);
    const prev = data.blanks ?? [];
    return map(Array.from({ length: n }), (_v, i) => {
        const id = blankIdForIndex(i);
        const existing = prev[i];
        if (existing) {
            return { ...existing, id };
        }
        return createDefaultBlank(id);
    });
}

export function normalizeFITBData(data: Partial<FITBElementData>): FITBElementData {
    const passage =
        data.passage && typeof data.passage === 'object' && data.passage.type === 'doc'
            ? data.passage
            : { type: 'doc' as const, content: [{ type: 'paragraph' }] };
    const withPassage = { ...data, passage };
    const blanks = syncBlanksWithPassage(withPassage);
    return {
        passage,
        blanks,
        explanation: data.explanation ?? '',
        gradingCaseSensitive: data.gradingCaseSensitive ?? false,
    };
}

export const defaultFITBElementData: FITBElementData = normalizeFITBData({});

export const defaultFITBConfig: FITBConfig = {
    ...getDefaultConfig(),
    blankMode: 'type',
    caseSensitive: false,
    shuffleOptions: true,
};

export function getFITBBlankMode(
    config?: ContentConfig | FITBConfig | FITBElementPlayerConfig
): FITBBlankMode {
    if (config != null && isWithBlankMode(config)) {
        const mode = config.blankMode;
        if (mode === 'drag' || mode === 'dropdown' || mode === 'type') return mode;
    }
    return 'type';
}

export function isCaseSensitive(
    config?: ContentConfig | FITBConfig | FITBElementPlayerConfig
): boolean {
    if (config != null && isWithCaseSensitive(config)) {
        return Boolean(config.caseSensitive);
    }
    return false;
}

export function shouldShuffleOptions(
    config?: ContentConfig | FITBConfig | FITBElementPlayerConfig
): boolean {
    if (config == null || typeof config !== 'object') return true;
    if (isWithShuffleOptions(config)) {
        return config.shuffleOptions !== false;
    }
    return true;
}

export const fitbElementSchema: ElementSchema<FITBElementData> = {
    type: 'element-fill-in-the-blanks',
    title: 'Fill in the Blanks',
    description: 'Rich-text passage with blanks (___); drag-and-drop, dropdown, or type answers',
    version: '1.0.0',
    category: 'question',
    icon: 'TextCursorInput',
    gradable: true,
    tags: ['fitb', 'blanks', 'cloze', 'assessment'],
    fields: [
        {
            name: 'passage',
            type: 'group',
            label: 'Passage',
            description: 'Rich text; use ___ (3+ underscores) for each blank',
            required: true,
        } as GroupFieldSchema,
        {
            name: 'blanks',
            type: 'list',
            label: 'Blanks',
            required: true,
            minItems: 1,
        } as ListFieldSchema,
        {
            name: 'explanation',
            type: 'text',
            label: 'Explanation',
            multiline: true,
        } as TextFieldSchema,
    ],
    defaultData: defaultFITBElementData,
    i18n: defaultFitbElementI18n,
};
